import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import parse from "html-react-parser"

import chevronLeftBlack from "images/layout/chevron-left-black.svg?raw"
import PaymentInfoForm from "./payment_info_form"
import Success from "./success"
import ChevronUpIcon from "shared/components/icons/chevron_up_icon"
import ChevronDownIcon from "shared/components/icons/chevron_down_icon"
import ErrorBoundary from "shared/components/error_boundary"
import { tierDisplayName } from "shared/utils/tiers"
import { lockupLogoSvg } from "shared/utils/ifttt_logo"
import { formatCreditsPerMonth } from "app/components/credits_upgrade/plan_summary"

import styles from "./sign_up_and_purchase.module.scss"
import creditsStyles from "./sign_up_and_purchase_credits.module.scss"

const SHARED_FEATURES = ["Multi-action Applets", "AI features", "Unlimited Applets", "All services"]
const CHECKOUT_FEATURES = {
  credits_starter: [...SHARED_FEATURES, "Standard speeds", "Basic support"],
  credits_pro: [...SHARED_FEATURES, "Priority speeds", "Prioritized support"],
  credits_pro_plus: [...SHARED_FEATURES, "Best speeds", "Dedicated support"],
}

export default function SignUpAndPurchaseCredits(props) {
  useEffect(() => {
    if (props.inactiveUserAppletsDisabled) window.App.Utils?.logCustomDatadogAction("inactive_applets_disabled")
    window.App.Utils?.logCustomDatadogAction("stripe_checkout_viewed") // leaving this alone in case other funnels rely on it

    window.App.Utils?.logCustomDatadogAction("stripe_checkout_initial")
    window.App.Utils?.logCustomDatadogAction("stripe_checkout_initial_non_modal")
  }, [])

  const [subscription, setSubscription] = useState(null)
  const [permissions, setPermissions] = useState(null)

  const tierName = tierDisplayName(props.tier)
  let tierLogo
  switch (props.tier) {
    case "credits_pro":
      tierLogo = <img src={props.assets.proBadgeSvgUrl} alt="Pro badge" width="91" height="53" />
      break
    case "credits_pro_plus":
      tierLogo = <img src={props.assets.proPlusBadgeSvgUrl} alt="Pro+ badge" width="91" height="53" />
      break
    case "credits_starter":
      tierLogo = <img src={props.assets.starterBadgeSvgUrl} alt="Starter badge" width="124" height="53" />
  }

  return (
    <>
      {subscription ? (
        <ErrorBoundary>
          <Success
            urls={props.urls}
            subscription={{
              isTrialing: false,
              expiresAt: subscription.expires_at,
              plan: subscription.plan_id,
            }}
            permissions={permissions}
            subscriptionMobileSuccessPath={
              props.redirectToMobileAppOnSuccess ? "ifttt://pro_subscription?status=subscribed" : null
            }
          />
        </ErrorBoundary>
      ) : (
        <ErrorBoundary>
          <header className={`${styles["onboarding-header"]} pb2 pb0--phone`}>
            <div className={styles["spacer"]}>
              {!props.renderingInsideModal && (
                <button
                  type="submit"
                  className="button-tertiary button-outlined button-with-icon only-icon-on-mobile"
                  aria-label="Back button"
                  data-dd-action-name="Subscription back button"
                  onClick={() => {
                    window.history.back()
                  }}
                >
                  <span className="button-icon">{parse(chevronLeftBlack)}</span>
                  <span className="hide-on-xs">Back</span>
                </button>
              )}
            </div>
            <h1>
              {parse(lockupLogoSvg())}
              {tierLogo}
            </h1>
            <div className={styles["spacer"]} />
          </header>

          <div className={creditsStyles["column"]}>
            <section className={creditsStyles["plan-details"]}>
              {props.creditsPerMonth != null && (
                <div className={creditsStyles["credits-allowance"]}>{formatCreditsPerMonth(props.creditsPerMonth)}</div>
              )}
              <ul className={creditsStyles["features"]}>
                {(CHECKOUT_FEATURES[props.tier] || []).map(feature => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </section>
            <PaymentInfoForm
              {...props}
              creditsCheckout
              ctaCopy={`Upgrade to ${tierName}`}
              studentProgramCheckout={false}
              setSubscription={setSubscription}
              setPermissions={setPermissions}
            />
          </div>
        </ErrorBoundary>
      )}
    </>
  )
}

SignUpAndPurchaseCredits.propTypes = {
  creditsPerMonth: PropTypes.number,
  assets: PropTypes.shape({
    proBadgeSvgUrl: PropTypes.string.isRequired,
    proPlusBadgeSvgUrl: PropTypes.string.isRequired,
    starterBadgeSvgUrl: PropTypes.string.isRequired,
  }),
  eligibleForTrial: PropTypes.bool,
  eligibleTrialPeriod: PropTypes.number,
  inactiveUserAppletsDisabled: PropTypes.bool,
  redirectToMobileAppOnSuccess: PropTypes.bool,
  renderingInsideModal: PropTypes.bool.isRequired,
  showWarningForStudents: PropTypes.bool,
  tier: PropTypes.string.isRequired,
  urls: PropTypes.object,
  userIsStudent: PropTypes.bool,
  initialInterval: PropTypes.string,
}

SignUpAndPurchaseCredits.defaultProps = {
  renderingInsideModal: false,
  showWarningForStudents: false,
  userIsStudent: false,
}
